// src/components/PremiumUpgradeModal.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PAYMENT_PLANS, createCheckoutSession } from '../services/paymentService';

function PremiumUpgradeModal({ isOpen, onClose, onSuccess }) {
  const [selectedPlan, setSelectedPlan] = useState(PAYMENT_PLANS.MONTHLY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { currentUser } = useAuth();

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  // Handle plan selection
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  // Handle next step in upgrade process
  const handleNextStep = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const userId = currentUser?.uid;
      const userEmail = currentUser?.email;
      
      if (!userId || !userEmail) {
        throw new Error('User not authenticated');
      }
      
      // Log details before making the call for easier debugging
      console.log('Creating checkout session with plan:', selectedPlan, 'user ID:', userId, 'email:', userEmail);
      
      // Make the API call to create a checkout session
      const { url } = await createCheckoutSession(selectedPlan, userId, userEmail);
      
      // Redirect to Stripe checkout
      if (url) {
        console.log('Redirecting to Stripe checkout:', url);
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to initialize checkout. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4"
      style={{ zIndex: 999999 }}
      onClick={onClose}
    >
      {/* Modal Content */}
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto mt-[10vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>

        {/* Content */}
        <div className="pr-8">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Upgrade to Pro</h2>
          <p className="text-gray-600 mb-6">
            Choose a subscription plan to unlock all Pro features, including unlimited workout saves,
            favorite exercises, and enhanced progress tracking.
          </p>
          
          <div className="space-y-4 mb-8">
            {/* Monthly Plan */}
            <div
              className={`border rounded-lg p-4 transition-all cursor-pointer ${
                selectedPlan.id === PAYMENT_PLANS.MONTHLY.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
              onClick={() => handleSelectPlan(PAYMENT_PLANS.MONTHLY)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{PAYMENT_PLANS.MONTHLY.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">{PAYMENT_PLANS.MONTHLY.description}</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-gray-800">
                    ${PAYMENT_PLANS.MONTHLY.price}
                  </span>
                  <span className="text-gray-500 text-sm">/month</span>
                </div>
              </div>
            </div>
            
            {/* Annual Plan */}
            <div
              className={`border rounded-lg p-4 transition-all cursor-pointer ${
                selectedPlan.id === PAYMENT_PLANS.ANNUAL.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
              onClick={() => handleSelectPlan(PAYMENT_PLANS.ANNUAL)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{PAYMENT_PLANS.ANNUAL.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">{PAYMENT_PLANS.ANNUAL.description}</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-gray-800">
                    ${PAYMENT_PLANS.ANNUAL.price}
                  </span>
                  <span className="text-gray-500 text-sm">/year</span>
                </div>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
              {error}
            </div>
          )}
          
          <div className="flex justify-between">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center disabled:bg-blue-400 disabled:cursor-not-allowed"
              onClick={handleNextStep}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : 'Continue to Checkout'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PremiumUpgradeModal;